import { useEffect, useMemo, useState } from "react";
import api, { resolveUrl, formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import Modal from "@/components/admin/Modal";
import { Field, TextInput, TextArea, Select, Toggle } from "@/components/admin/FormControls";
import ImageUploader from "@/components/admin/ImageUploader";
import FileUploader from "@/components/admin/FileUploader";
import {
    Plus, Pencil, Trash2, Save, Loader2, BookOpen, Search, Lock, Gift, Tag as TagIcon, Sparkles, Flame, ExternalLink, FileText, Upload, Download,
} from "lucide-react";

const emptyEbook = {
    title: "", description: "", cover: "", url: "", category: "",
    price_brl: 0, access_type: "premium", featured_tag: "",
    available_for_upsell: true,
};

const ACCESS_OPTIONS = [
    { value: "free", label: "Grátis (qualquer um baixa)" },
    { value: "premium", label: "Premium (apenas assinantes)" },
    { value: "paid", label: "Avulso (compra individual)" },
];
const TAG_OPTIONS = [
    { value: "", label: "Nenhuma" },
    { value: "new", label: "Lançamento" },
    { value: "trending", label: "Em alta" },
];

function BRL(v) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

function AccessBadge({ type }) {
    const map = {
        free: { label: "Grátis", cls: "bg-[#00D4D4]/15 text-[#0a7a7a]", Icon: Gift },
        premium: { label: "Premium", cls: "bg-[#9370DB]/15 text-[#5a3fa0]", Icon: Lock },
        paid: { label: "Avulso", cls: "bg-[#D4AF37]/15 text-[#8a7022]", Icon: TagIcon },
    };
    const cfg = map[type] || map.paid;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-widest font-display uppercase ${cfg.cls}`}>
            <cfg.Icon className="w-3 h-3" /> {cfg.label}
        </span>
    );
}

function FeaturedBadge({ tag }) {
    if (tag === "new") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1A1A1A] text-white text-[10px] tracking-widest font-display uppercase"><Sparkles className="w-3 h-3" /> Lançamento</span>;
    if (tag === "trending") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF6B6B]/15 text-[#c4453d] text-[10px] tracking-widest font-display uppercase"><Flame className="w-3 h-3" /> Em alta</span>;
    return null;
}

function StatCard({ label, value, accent = "from-white to-[#FAFAF7]" }) {
    return (
        <div className={`rounded-2xl p-5 border border-black/5 bg-gradient-to-br ${accent}`}>
            <div className="overline text-[#5C5C5C]">{label}</div>
            <div className="font-display font-light text-3xl tracking-tighter mt-2">{value}</div>
        </div>
    );
}

export default function AdminLibrary() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all"); // all|free|premium|paid|new|trending
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyEbook);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/admin/ebooks").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm(emptyEbook); setEditing(null); setError(""); setOpen(true); };
    const openEdit = (e) => { setForm({ ...emptyEbook, ...e }); setEditing(e.id); setError(""); setOpen(true); };

    const save = async () => {
        setError(""); setSaving(true);
        try {
            const payload = {
                ...form,
                price_brl: Number(form.price_brl) || 0,
                access_type: form.access_type || "premium",
                featured_tag: form.featured_tag || "",
                available_for_upsell: form.available_for_upsell !== false,
            };
            if (editing) await api.put(`/admin/ebooks/${editing}`, payload);
            else await api.post("/admin/ebooks", payload);
            setOpen(false); await load();
        } catch (e) { setError(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Excluir este e-book?")) return;
        try { await api.delete(`/admin/ebooks/${id}`); await load(); }
        catch (e) { alert(formatApiError(e)); }
    };

    const filtered = useMemo(() => {
        return items.filter((e) => {
            if (search && !`${e.title} ${e.category} ${e.description}`.toLowerCase().includes(search.toLowerCase())) return false;
            if (filter === "all") return true;
            if (filter === "new" || filter === "trending") return e.featured_tag === filter;
            return (e.access_type || "paid") === filter;
        });
    }, [items, filter, search]);

    const stats = useMemo(() => ({
        total: items.length,
        free: items.filter((e) => e.access_type === "free").length,
        premium: items.filter((e) => e.access_type === "premium").length,
        paid: items.filter((e) => e.access_type === "paid").length,
        downloads: items.reduce((sum, e) => sum + (e.downloads || 0), 0),
    }), [items]);

    const showPriceField = form.access_type !== "free";

    return (
        <AdminLayout eyebrow="Conteúdo" title="Biblioteca digital">
            <p className="font-body text-[#5C5C5C] mb-8">Cadastre e-books, defina o tipo de acesso e faça upload direto do PDF — sem precisar de hospedagem externa.</p>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-10">
                <StatCard label="Total" value={stats.total} accent="from-[#F2EFEA] to-white" />
                <StatCard label="Grátis" value={stats.free} accent="from-[#00D4D4]/15 to-white" />
                <StatCard label="Premium" value={stats.premium} accent="from-[#9370DB]/15 to-white" />
                <StatCard label="Avulso" value={stats.paid} accent="from-[#D4AF37]/15 to-white" />
                <StatCard label="Downloads" value={stats.downloads.toLocaleString("pt-BR")} accent="from-[#1A1A1A]/5 to-white" />
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 text-[#5C5C5C] absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                        data-testid="library-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por título, categoria ou descrição..."
                        className="imq-input !pl-11"
                    />
                </div>
                <button onClick={openCreate} data-testid="ebook-create-btn" className="btn-primary whitespace-nowrap">
                    <Plus className="w-4 h-4" /> Novo e-book
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-8">
                {[
                    { id: "all", label: "Tudo" },
                    { id: "free", label: "Grátis" },
                    { id: "premium", label: "Premium" },
                    { id: "paid", label: "Avulso" },
                    { id: "new", label: "Lançamentos" },
                    { id: "trending", label: "Em alta" },
                ].map((f) => (
                    <button
                        key={f.id}
                        data-testid={`library-filter-${f.id}`}
                        onClick={() => setFilter(f.id)}
                        className={`px-4 py-2 rounded-full text-sm font-display transition-colors ${
                            filter === f.id ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/5 hover:border-black/20"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {/* Grid */}
            <div data-testid="admin-library-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((e) => (
                    <article key={e.id} data-testid={`ebook-card-${e.id}`} className="group bg-white rounded-2xl border border-black/5 overflow-hidden hover:border-black/20 transition-all">
                        <div className="relative aspect-[4/3] bg-[#FAFAF7] overflow-hidden">
                            {e.cover && <img src={resolveUrl(e.cover)} alt={e.title} className="w-full h-full object-cover" loading="lazy" />}
                            <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1.5">
                                <AccessBadge type={e.access_type || "paid"} />
                                <FeaturedBadge tag={e.featured_tag} />
                            </div>
                            {e.url && (
                                <a href={resolveUrl(e.url)} target="_blank" rel="noreferrer" className="absolute bottom-2.5 right-2.5 bg-white/95 rounded-full p-1.5 hover:bg-white" title="Abrir PDF" data-testid={`ebook-openpdf-${e.id}`}>
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            )}
                        </div>
                        <div className="p-4">
                            <div className="overline text-[#5C5C5C]">{e.category || "—"}</div>
                            <h3 className="font-display text-base mt-1 tracking-tight line-clamp-2">{e.title}</h3>
                            <div className="flex items-center justify-between mt-3">
                                <div className="font-display text-sm">{e.access_type === "free" ? <span className="text-[#00D4D4]">Grátis</span> : BRL(e.price_brl)}</div>
                                <div className="flex gap-1">
                                    <button onClick={() => openEdit(e)} data-testid={`ebook-edit-${e.id}`} className="p-1.5 rounded-lg hover:bg-black/5"><Pencil className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => remove(e.id)} data-testid={`ebook-delete-${e.id}`} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                            <div className="flex items-center justify-between mt-2 text-[11px] font-body text-[#5C5C5C]">
                                <span data-testid={`ebook-downloads-${e.id}`} className="inline-flex items-center gap-1">
                                    <Download className="w-3 h-3" /> {(e.downloads || 0).toLocaleString("pt-BR")} downloads
                                </span>
                                {!e.url ? (
                                    <span className="text-amber-700 inline-flex items-center gap-1">
                                        <FileText className="w-3 h-3" /> Sem arquivo
                                    </span>
                                ) : (e.access_type !== "free" && e.available_for_upsell === false) ? (
                                    <span data-testid={`ebook-noupsell-${e.id}`} className="inline-flex items-center gap-1 text-[#9A9A9A]" title="Excluído do upsell">
                                        <Sparkles className="w-3 h-3" /> Fora do upsell
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </article>
                ))}
                {filtered.length === 0 && (
                    <div className="col-span-full text-center py-16 font-body text-[#5C5C5C] bg-white rounded-2xl border border-black/5">
                        <BookOpen className="w-7 h-7 mx-auto mb-3 text-[#9A9A9A]" />
                        {items.length === 0 ? "Nenhum e-book cadastrado ainda. Clique em \"Novo e-book\" para começar." : "Nenhum resultado para este filtro."}
                    </div>
                )}
            </div>

            {/* Modal editor */}
            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar e-book" : "Novo e-book"} size="xl">
                <div className="grid md:grid-cols-[260px_1fr] gap-6">
                    {/* Left: cover uploader */}
                    <div>
                        <div className="overline text-[#5C5C5C] mb-2">Capa</div>
                        <ImageUploader testId="ebook-cover" value={form.cover} onChange={(url) => setForm({ ...form, cover: url })} aspect="aspect-[5/4]" />
                    </div>

                    {/* Right: fields */}
                    <div className="space-y-4">
                        <Field label="Título"><TextInput testId="ebook-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Manual Prático da Medicina Quântica" /></Field>
                        <Field label="Descrição curta"><TextArea testId="ebook-desc" rows={3} value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="Resumo que aparece nos cards e na biblioteca pública" /></Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Categoria"><TextInput testId="ebook-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="Ex.: Consciência" /></Field>
                            <Field label="Tag de destaque"><Select testId="ebook-tag" value={form.featured_tag} onChange={(v) => setForm({ ...form, featured_tag: v })} options={TAG_OPTIONS} placeholder="Nenhuma" /></Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Tipo de acesso" hint="Define quem consegue baixar o arquivo"><Select testId="ebook-access" value={form.access_type} onChange={(v) => setForm({ ...form, access_type: v || "premium" })} options={ACCESS_OPTIONS} placeholder="Selecione..." /></Field>
                            <Field label="Preço (R$)" hint={showPriceField ? "Usado para compra avulsa e upsell" : "Não se aplica a e-books gratuitos"}>
                                <TextInput testId="ebook-price" type="number" value={form.price_brl} onChange={(v) => setForm({ ...form, price_brl: v })} min={0} step="0.01" disabled={!showPriceField} />
                            </Field>
                        </div>

                        {showPriceField && (
                            <div className="bg-[#D4AF37]/8 border border-[#D4AF37]/20 rounded-xl p-4">
                                <Toggle
                                    testId="ebook-upsell"
                                    checked={form.available_for_upsell !== false}
                                    onChange={(v) => setForm({ ...form, available_for_upsell: v })}
                                    label={form.available_for_upsell !== false ? "Disponível para upsell pós-pagamento" : "Excluído da oferta de upsell"}
                                />
                                <div className="text-[11px] font-body text-[#5C5C5C] mt-2 ml-12">
                                    Quando ativo, este e-book aparece com desconto na tela de confirmação de pagamento de planos.
                                </div>
                            </div>
                        )}

                        <div className="border-t border-black/5 pt-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="overline text-[#5C5C5C]">Arquivo do e-book (PDF)</div>
                                {form.url && (
                                    <a href={resolveUrl(form.url)} target="_blank" rel="noreferrer" className="text-xs font-display text-[#5C5C5C] hover:text-[#1A1A1A] inline-flex items-center gap-1">
                                        <ExternalLink className="w-3 h-3" /> Abrir arquivo
                                    </a>
                                )}
                            </div>
                            <FileUploader
                                testId="ebook-file"
                                value={form.url}
                                onChange={(url) => setForm({ ...form, url })}
                                accept=".pdf,.epub,application/pdf"
                            />
                            <div className="text-[11px] font-body text-[#5C5C5C] mt-2 inline-flex items-center gap-1.5">
                                <Upload className="w-3 h-3" /> Aceita PDF/EPUB até 50 MB. O arquivo fica protegido conforme o tipo de acesso configurado acima.
                            </div>
                        </div>

                        {error && <div data-testid="ebook-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>}

                        <div className="flex justify-end gap-3 pt-3">
                            <button onClick={() => setOpen(false)} className="btn-ghost">Cancelar</button>
                            <button
                                onClick={save}
                                data-testid="ebook-save"
                                className="btn-primary"
                                disabled={saving || !form.title?.trim()}
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </AdminLayout>
    );
}
